import React from 'react'
import { useOutletContext } from 'react-router-dom'
import { OutletContext } from './employer-settings-wrapper'
import { useUsersQuery } from '../../queries/users'
import SidebarContentTitle from '../../components/layout/sidebar/sidebar-content-title'
import UsersFilters, { UsersFiltersValues } from '../../components/users/filters'
import { Box, Element, Tabs } from 'react-bulma-components'
import WorkerLabel from '../../components/texts/worker-label'
import { User } from '../../api/users'
import UsersTable from '../../components/users/tables/users-table'
import { getWorkerRoleOrAssociationStatus } from '../../utils/users'
import EmployerWorkerExplanation from '../../components/users/employer-worker-explanation'
import CreateOfflineWorkerButton from '../../components/users/buttons/create-offline-worker'
import InviteUserButton from '../../components/users/buttons/invite-user'
import BulkActionsButton from '../../components/users/bulk-actions-dropdown'
import BulkContractualSchedulesModal from '../../components/users/modals/bulk-contractual-schedules-modal'
import { useModal } from '../../components/modal'

/**
 * Component for managing employer workers
 */
const WorkersSettings: React.FC = () => {
  const { employer } = useOutletContext<OutletContext>()
  const [filters, setFilters] = React.useState<UsersFiltersValues>({
    // TODO: refactor using filters in store and in http requests
    status: ['accepted', 'pending'],
  })

  const [currentAppAccessTab, setCurrentAppAccessTab] = React.useState<User['appAccess']>('full')
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([])
  const bulkContractualSchedulesModal = useModal({})

  // Réinitialiser la sélection quand on change d'onglet
  React.useEffect(() => {
    setSelectedUserIds([])
  }, [currentAppAccessTab])

  const query = useUsersQuery({
    'roles.kind': 'worker',
    appAccess: currentAppAccessTab,
  })

  const tabs: { label: React.ReactNode; value: User['appAccess'] }[] = [
    {
      label: (
        <>
          <WorkerLabel /> Connecté
        </>
      ),
      value: 'full',
    },
    {
      label: (
        <>
          <WorkerLabel /> Offline
        </>
      ),
      value: 'none',
    },
  ]

  const users = (query.data || []).filter(user => {
    // TODO: refactor using filters in store and in http requests
    const roleOrAssociationStatus = getWorkerRoleOrAssociationStatus(user, 'worker', employer)
    return (
      !filters.status ||
      !filters.status[0] ||
      !roleOrAssociationStatus ||
      filters.status.includes(roleOrAssociationStatus)
    )
  })

  const selectedUsers = users.filter(user => selectedUserIds.includes(user._id))

  return (
    <>
      <SidebarContentTitle
        title="Salariés"
        help={<EmployerWorkerExplanation textColor="white" />}
        ActionButton={
          currentAppAccessTab === 'none' ? (
            <CreateOfflineWorkerButton />
          ) : (
            <InviteUserButton
              userRoleKind={'worker'}
              label="Ajouter un travailleur"
              modalTitle="Inviter un nouveau travailleur"
            />
          )
        }
      />
      <Box>
        <UsersFilters
          onChange={setFilters}
          initialValues={{
            status: ['accepted', 'pending'],
          }}
        />

        {currentAppAccessTab === 'full' && selectedUserIds.length > 0 && (
          <Element mb={3}>
            <BulkActionsButton
              selectedCount={selectedUserIds.length}
              onModifyContractualSchedules={() =>
                bulkContractualSchedulesModal.setIsDisplayed(true)
              }
            />
          </Element>
        )}

        <Tabs>
          {tabs.map(tab => (
            <Tabs.Tab
              key={tab.value}
              active={currentAppAccessTab === tab.value}
              onClick={() => setCurrentAppAccessTab(tab.value)}
              data-test={`workers-list-tab-${tab.value}`}
            >
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs>
        <UsersTable
          users={users}
          roleKind={currentAppAccessTab === 'none' ? 'offlineWorker' : 'worker'}
          enableSelection={currentAppAccessTab === 'full'}
          selectedUserIds={selectedUserIds}
          onSelectionChange={setSelectedUserIds}
        />
        {employer && (
          <BulkContractualSchedulesModal
            users={selectedUsers}
            organization={employer}
            modalActions={bulkContractualSchedulesModal}
            onSuccess={() => {
              setSelectedUserIds([])
            }}
          />
        )}
      </Box>
    </>
  )
}

export default WorkersSettings
